import { useEffect, useRef } from "react";
import "./FireworksCanvas.css";

export default function FireworksCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    let w, h, dpr;
    let animationId;
    let intervalId;

    const rockets = [];
    const fireworks = [];

    const gravity = 0.045;
    const friction = 0.985;
    const rocketFriction = 0.995;
    const PARTICLE_COUNT = 260;

    const FIRE_INTERVAL = 15000; 
    const ROCKETS_PER_BATCH = 2; 

    function resize() {
      dpr = window.devicePixelRatio || 1;
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    window.addEventListener("resize", resize);
    resize();

    function launchRocket(startX, startY, targetX, targetY, hue) {
      const dx = targetX - startX;
      const dy = targetY - startY;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;

      const speed = 5 + Math.random() * 2;
      rockets.push({
        x: startX,
        y: startY,
        vx: (dx / dist) * speed,
        vy: (dy / dist) * speed,
        targetY,
        baseHue: hue,
        trail: [],
      });
    }

    function spawnFirework(x, y, baseHue) {
      const particles = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 4 + Math.random() * 4;

        particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0,
          maxLife: 45 + Math.random() * 40,
          hue: baseHue + Math.random() * 40 - 20,
          brightness: 70 + Math.random() * 20,
          radius: 1 + Math.random(),
        });
      }
      fireworks.push({ particles });
    }

    function fireBatch() {
      for (let i = 0; i < ROCKETS_PER_BATCH; i++) {
        const tx = w * (0.25 + Math.random() * 0.5);
        const ty = h * (0.08 + Math.random() * 0.25);
        const sx = Math.random() < 0.5 ? -40 : w + 40;
        const sy = h + 30;
        launchRocket(sx, sy, tx, ty, Math.random() * 360);
      }
    }

    intervalId = setInterval(fireBatch, FIRE_INTERVAL);

    function loop() {
      animationId = requestAnimationFrame(loop);

      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,0.2)";
      ctx.fillRect(0, 0, w, h);

      ctx.globalCompositeOperation = "lighter";


      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.trail.push({ x: r.x, y: r.y });
        if (r.trail.length > 8) r.trail.shift();

        r.vx *= rocketFriction;
        r.vy *= rocketFriction;
        r.vy += gravity * 0.4;
        r.x += r.vx;
        r.y += r.vy;

        ctx.beginPath();
        r.trail.forEach((p, idx) =>
          idx === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)
        );
        ctx.strokeStyle = `hsla(${r.baseHue},100%,70%,1)`;
        ctx.lineWidth = 2;
        ctx.stroke();

        if (r.y <= r.targetY || r.vy >= 0) {
          spawnFirework(r.x, r.y, r.baseHue);
          rockets.splice(i, 1);
        }
      }

      // ===== PARTICLES =====
      for (let f = fireworks.length - 1; f >= 0; f--) {
        const fw = fireworks[f];
        for (let i = fw.particles.length - 1; i >= 0; i--) {
          const p = fw.particles[i];
          p.life++;
          if (p.life > p.maxLife) {
            fw.particles.splice(i, 1);
            continue;
          }

          p.vx *= friction;
          p.vy *= friction;
          p.vy += gravity * 0.6;
          p.x += p.vx;
          p.y += p.vy;

          const alpha = 1 - p.life / p.maxLife;
          ctx.strokeStyle = `hsla(${p.hue},100%,${p.brightness}%,${alpha})`;
          ctx.lineWidth = p.radius;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - p.vx * 2, p.y - p.vy * 2);
          ctx.stroke();
        }

        if (!fw.particles.length) fireworks.splice(f, 1);
      }
    }

    animationId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animationId);
      clearInterval(intervalId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="fireworks-canvas" />;
}
